import React from 'react';
import useSWR from 'swr';
import api from '../../services/api';
import { fetcher } from '../../services/fetcher';
import { moduleConfig } from './config';

export type RoleType = 'ADMIN' | 'USER' | 'CLIENT' | 'GUEST';

export interface User {
  id: number;
  userEmail: string;
  userDisplayName?: string;
  userPhotoPath?: string | null;
  userFirstName: string;
  userLastName: string;
  userIsActive: boolean;
  role: RoleType;
  createdAt: string;
  updatedAt?: string;
}

export interface UsersResponse {
  data: User[];
  total: number;
  page: number;
  limit: number;
}

export const useUsers = (searchParams?: string) => {
  const queryString = searchParams ? `?searchParams=${encodeURIComponent(searchParams)}` : '';
  const { data, error, isLoading, mutate } = useSWR<User[]>(
    `${moduleConfig.module.api.list}${queryString}`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data || [],
    users: data || [],
    total: data?.length || 0,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useUser = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<User>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    data: data,
    user: data,
    isLoading,
    isError: error,
    mutate,
  };
};

export function getUserPhotoUrl(userId: number): string {
  const base = api.defaults.baseURL ?? '';
  return `${base}/users/${userId}/photo`;
}

export function useUserPhotoBlobUrl(userId: number | undefined, photoPath: string | null | undefined) {
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const blobUrlRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!userId || !photoPath) {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setBlobUrl(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get(getUserPhotoUrl(userId), { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        if (res.status === 204 || (res.data instanceof Blob && res.data.size === 0)) {
          setBlobUrl(null);
          return;
        }
        const url = URL.createObjectURL(res.data as Blob);
        blobUrlRef.current = url;
        setBlobUrl(url);
      })
      .catch(() => {
        if (!cancelled) setBlobUrl(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setBlobUrl(null);
    };
  }, [userId, photoPath]);

  return { photoBlobUrl: blobUrl, loading };
}

export async function uploadUserPhoto(userId: number, file: File): Promise<{ userPhotoPath: string }> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post(`/users/${userId}/photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

export async function deleteUserPhoto(userId: number): Promise<{ userPhotoPath: null }> {
  const res = await api.delete(`/users/${userId}/photo`);
  return res.data;
}

